<template>
  <v-card flat class="faq">
    <v-subheader class="subheader">Frequently Asked Questions
    </v-subheader>
    <v-card-text>
      <template>
        <div class="mb-4"></div>
        <v-expansion-panels accordion focusable>
          <v-expansion-panel
            v-for="(item,i) in items"
            :key="i" @click.native="sendPanelClickDataToGA">
            <v-expansion-panel-header>
              <span v-html="item.title" class="py-2 font-weight-bold">{{item.title}}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-4">
              <span v-html="item.content">{{item.content}}</span>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
  export default {
    name: 'FAQ',
    data() {
      return {
        items: [
          {
            title:'1. How do I submit feedback?',
            content:'ICT PLUS is in maintenance mode. All support and feedback is provided through EDConnect.'
          },
          {
            title: '2. How do I share access to ICT PLUS?',
            content: 'Access to ICT PLUS is open to all school principals and field operations staff and principals can also delegate access to staff via AMU or MSA. Simply select "ICP - ICT PLUS+ Delegation" from the applications list on AMU/MSA and then follow the usual steps to delegate access.<br/><br/> Access to corporate staff must be justified and approved, please log a request with ED Connect.'
          },
          {
            title: '3. What type of information is displayed in ICT PLUS?',
            content: 'ICT PLUS sources data from multiple existing systems, with the benefit of making the various sources accessible in one place.<br/>Click on the “i” icon at the top of each page for an overview of what the page provides, this gives you helpful context.<br/><br/>See the “Data Sources” tab above for information on where the data in ICT PLUS comes from and how often it’s updated.<br/>Visit the “Data Glossary” tab for descriptions of the different data fields we show in each page of ICT PLUS. This page will also let you know whether a particular type of information in the Managed Devices page is not available for Windows/Apple/Chrome devices (and therefor appear as “Data Not Available” in the Devices table).'
          },
          {
            title: '4. Where does the data in ICT PLUS come from? How often is it updated?',
            content: 'The department has a treasure trove of machine and user data available to us. We connect, at scheduled intervals, to various sources of data. See the “Data Sources” tab for more information.'
          },
          {
            title: "5. My data doesn't look quite right, what should I do?",
            content: 'ICT PLUS sources data from multiple existing systems, with the benefit of making the various sources accessible in one place. If you’ve found that the data in ICT PLUS doesn\'t line up to what you\'ve found in your school, please let us know and we\'ll escalate your concern to the relevant ITD team.<br/><br/>' +
              'Log a ticket with ED Connect, or contact us directly via the <strong><i>Feedback</i></strong> button in the left sidebar of ICT PLUS. Please mention your name, email address, school name, school code, and the names of a few example devices that exhibit the data issue.'
          },
          {
            title: '6. Why does the Managed Devices page only show some, not all, of my devices?',
            content: 'Currently, we are limited to obtaining accurate “managed” device data for Google, Microsoft and Apple devices. “Managed” means machines that are connected to the network and run the Departments Standard Operating Environment (SOE) image.<br/><br/>For Microsoft Managed Devices, ICT PLUS determines location using the *current AD Site*, which will be the network the device is on at the time it reports in. "Reporting in" is when the device sends back status or inventory. In some cases it might even be that the device was offline at the time it ran the inventory and sends this the next time it’s online. For this reason, if a particular device is used at multiple schools, then in ICT PLUS the device will not be displayed in the device list of the school that owns the device and may appear in another school’s device list.'
          },
          {
            title: '7. When I submit feedback, what happens next?',
            content: 'Your feedback is important to us. So important that it goes directly to the development team. We prioritise your feedback and correlate it with other user feedback. This builds the future features and enhancements in the product, which we communicate in our <a href="https://ictplus.education.nsw.gov.au/#/roadmap">Roadmap</a>.'
          },
          {
            title: '8. Why aren’t my column preferences in the Devices tab being saved?',
            content: 'Column preferences are saved to your device’s local storage, so if you log in to ICT PLUS from a different device, you won’t see the column preferences you have set when logging in from other devices. Furthermore, if more than one person logs in to ICT PLUS on the same device then the Device List will show the column preferences most recently set by the last logged in user.<br><br>' +
              'If you set your column preferences, to show/hide particular columns, then these preferences will be saved even after you navigate away from the Devices page, switch to another school, log out and log back in, or close your browser window/session and open ICT PLUS again.'
          },
          {
            title:'9. What browser should I be using?',
            content:'ICT PLUS works best in the Chrome browser.'
          },
          {
            title: '10. Glossary of Terms',
            content: `
<strong>Age of Model </strong> | This chart is based on the “Model Release Date” column from the Devices table, to show the amount of time since the specific model of your device has released. Note, this is not the date that the device was purchased.
<br><br>
<strong>Bandwidth</strong> | Bandwidth is the amount of data (MB) that can be transmitted in a fixed amount of time (ps). i.e. Mbps.
<br><br>
<strong>Last Seen (Days Ago)</strong> | This refers to a time-based field from Configuration Manager (CM) for Microsoft devices, which displays the time a device last checked in to the DoE network.
<br><br>
<strong>Total FTE Students</strong> | FTE = Full-Time Equivalence. An FTE is a measurement equal to one student enrolled full time for one academic year. Total FTE enrolment includes full time students plus the calculated equivalent of the part-time enrolments.
<br><br>
<strong>Utilisation</strong> | Usage. The dictionary describes Utilisation as “the practical and effective use of something”, essentially it’s a technical word for Usage – we’ll be changing this in the next release for ease of reading.
<br><br>
<strong>WAN Utilisation Inbound / Outbound</strong> | A wide area network (WAN) is a network that exists over a large-scale geographical area. Inbound Bandwidth is consumed when data is coming into the server (e.g. if a student or teacher uploads a file to a website). Outbound Bandwidth is consumed when the server sends assets to the visitor (e.g. if a student or teacher downloads a file from a website).
`
          },
        ]
      };
    },
    methods: {
      sendPanelClickDataToGA(e) {
        const targetElement = e.target;
        if (targetElement && targetElement.innerText) {
          const innerText = targetElement.innerText;
          console.log('FAQ:' + innerText);
          this.$ga.event({
            eventCategory: 'FAQ',
            eventAction: 'panel-click',
            eventLabel: `panel: ${innerText}`
          });
        }
      },
      sendLinkClickDataToGA(e) {
        const targetElement = e.target;
        if (targetElement && targetElement.innerText) {
          const innerText = targetElement.innerText;
          console.log('FAQ:' + innerText);
          this.$ga.event({
            eventCategory: 'FAQ',
            eventAction: 'link-click',
            eventLabel: `link: ${innerText}`
          });
        }
      }
    }
  };
</script>
<style scoped>
.faq{
  padding:0;
  background: #FFFFFF;
}
.subheader{
font-family: Arial;
font-style: normal;
font-weight: bold;
font-size: 24px;
line-height: 30px;
color: #041E42;
padding:0;
}
.v-card__text{
  padding: 0;
}
</style>
